import { useEffect, useState } from 'react';
import axios from 'axios';
import UniversityCard from '@/components/UniversityCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UniversitiesPage() {
  const [universities, setUniversities] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [countryFilter, setCountryFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await axios.get(`${API}/universities?limit=50`);
        setUniversities(response.data);
        setFilteredUniversities(response.data);
      } catch (error) {
        console.error('Error fetching universities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  useEffect(() => {
    if (countryFilter === 'all') {
      setFilteredUniversities(universities);
    } else {
      setFilteredUniversities(universities.filter(uni => uni.country === countryFilter));
    }
  }, [countryFilter, universities]);

  const countries = ['all', ...new Set(universities.map(uni => uni.country))];

  return (
    <div className="pt-[100px]">
      {/* Hero Header */}
      <div className="bg-primary py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="label-tag text-[hsl(var(--gold))] mb-3 block">Explore</span>
            <h1 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-4">
              World-Class Universities
            </h1>
            <p className="text-lg text-white/70">
              Discover top-ranked institutions from around the globe and find your perfect academic home
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section-padding bg-white">
        <div className="container-custom">
          {/* Filter Bar */}
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-100">
            <p className="text-gray-500 text-sm">
              Showing <span className="font-semibold text-primary">{filteredUniversities.length}</span> universities
            </p>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-48 rounded-sm border-gray-200" data-testid="country-filter">
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>
                    {country === 'all' ? 'All Countries' : country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="universities-grid">
              {filteredUniversities.map((uni) => (
                <UniversityCard key={uni.university_id} university={uni} />
              ))}
            </div>
          )}

          {!loading && filteredUniversities.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No universities found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
